import { Component, OnInit, OnDestroy, Input, HostListener, ViewChild, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import { SearchService } from '../../../../shared/search/search.service';
import { SearchEvent } from '../../../../shared/search/models/search-event';
import { NEW_SEARCH_EVENT, NEW_ROW_EVENT, PAGE_OVERFLOW_EVENT } from '../../../../shared/search/search-constants';
import { ResourceService } from 'sce-core';
import { UserService } from '../../../../core/security/user.service';
import { NavigationService } from '../../../../core/navigation.service';
import { AlertService } from 'sce-core';
import { ConfirmDialogService } from 'sce-core';
import { DetailViewKeyEventHandlerService } from '../../../../shared/services/detail-view-key-event-handler.service';
import { SearchableEntityConfig } from '../../../models/searchable-entity-config';
import { SearchableEntityServiceFactory } from '../../../services/searchable-entity/searchable-entity-service-factory';
import { AccordionConfig } from '../../../models/accordion-config';
import { ActivatedRoute } from '@angular/router';
import { LeftPanelService } from '../../../../layout/home/services/left-panel.service';
import { CoreServiceRegistry } from '../../../../core/core-service-registry';
import { SharedServiceRegistry } from '../../../shared-service-registry';
import { DetailedEntityDataService } from '../../../services/searchable-entity/detailed-entity/detailed-entity-data.service';
import { DetailedEntityButtonService } from '../../../services/searchable-entity/detailed-entity/detailed-entity-button.service';
import { DetailedEntityDataEditorService } from '../../../services/searchable-entity/detailed-entity/detailed-entity-data-editor.service';
import { Preference } from '../../../search/models/preference.model';
import { UserPreferenceService } from '../../../../layout/home/services/user-preference.service';
import { TablePreference } from '../../../search/models/table-preference.model';

@Component({
  selector: 'lfwms-searchable-entity-details',
  templateUrl: './details-page.component.html',
  styleUrls: ['./details-page.component.css']
})
export class SearchableEntityDetailsComponent implements OnInit, OnDestroy {

  @Input() public serviceFactory: SearchableEntityServiceFactory;

  // generic services
  protected dataService: DetailedEntityDataService;
  protected buttonService: DetailedEntityButtonService;
  protected dataEditor: DetailedEntityDataEditorService;

  // Service to maintain Master Data for Primary & Secondary Accordions in the Details Page
  protected primarySearchService: SearchService;
  // Search Service which is passed as input to Data Table inside Details Accordion
  protected detailsSearchService: SearchService;
  // Master Definition
  private masterDefinition: any;
  private columnConfigPreference: Preference;
  private userPreferences: any;
  private accordionUserPreference: any;

  /* Map Object <K,V> to maintain Cell Metadata [ for Primary & Secondary Accordions ] in the Details Page.
     This metadata is readily available from the Data Service and hence no fresh fetch is required. */
  public metadataMap: any;
  /* Map Object <K,V> to maintain Cell Data [ for Primary & Secondary Accordions ] in the Details Page.
     A fresh fetch is fired each time against a Receipt Key (dataService.primarySelectedDocument) from ngOnInit() */
  public cellDataMap: any;

  private keyEventHandler: DetailViewKeyEventHandlerService;

  public isLoading: boolean = false;

  public isPrimaryDataLoaded: boolean = false;

  // variable to store all object level sunscriptions. This will be unsubscribed on component destroy
  private subscriptionLists: Array<Subscription>;

  // Object to maintain the layout of each accordion as a simple JSON Object
  public accordionConfig: any;
  private reorderSourceKey: string;
  // Flag to show/hide Details Accordion
  public showDetailsAccordion: boolean = false;
  // Flag to expand/collapse Secondary Accordion
  public expandSecondaryAccordion: boolean = true;
  // Flag to expand/collapse Details Accordion
  public expandDetailsAccordion: boolean = true;

  protected entityConfig: SearchableEntityConfig;
  // To display screen labels
  public screenLabel: String;

  public primaryAccordion: AccordionConfig;
  public secondaryAccordionList: Array<AccordionConfig>;
  // flag indicating if the state should be retained
  private retainState: boolean;
  // variables for smooth scrolling of floating menu
  private floatingMenuOffset;
  public isStickyFloatingMenu: boolean;
  private previousScroll = 0;
  private isScrollDown = false;

  protected navService: NavigationService;
  protected resourceService: ResourceService;
  protected userService: UserService;
  // private activatedRoute: ActivatedRoute;
  protected alertService: AlertService;
  protected confirmDialogService: ConfirmDialogService;
  protected tablePreferences: UserPreferenceService;
  private tablePreference: TablePreference;
  // variable to decide whether to display secondary accordion or not
  private dispSecAcc = false;

  @ViewChild('floatingMenuId', { read: ElementRef }) navbarif: ElementRef;
  @HostListener('window:scroll', ['$event'])
  onScroll(event) {
    this.floatingMenuOffset = this.navbarif.nativeElement.offsetTop;
    if (this.previousScroll < window.pageYOffset) {
      this.isScrollDown = true;
    } else {
      this.isScrollDown = false;
    }
    this.previousScroll = window.pageYOffset;
    // floating menu is getting fixed when height is 40 px from top, that is why we are adding/subtracting 39 px
    // to the offset. So that there wont be any sudden jumps during scrolling.
    if ((window.pageYOffset + (this.isScrollDown ? 39 : -39)) >= (this.floatingMenuOffset)) {
      this.isStickyFloatingMenu = true;
    } else {
      this.isStickyFloatingMenu = false;
    }
  }

  constructor(private coreServiceRegistry: CoreServiceRegistry, private sharedServiceRegistry: SharedServiceRegistry,
    public leftPanelService: LeftPanelService, private activatedRoute: ActivatedRoute) {
    this.navService = coreServiceRegistry.navigationService;
    this.resourceService = coreServiceRegistry.resourceService;
    this.userService = coreServiceRegistry.userService;
    this.alertService = sharedServiceRegistry.alertService;
    this.confirmDialogService = sharedServiceRegistry.confirmDialogService;
    this.tablePreferences = sharedServiceRegistry.userPreferencesService;
    //  this.activatedRoute = coreServiceRegistry.activatedRoute;

    // Initializing Master Definition
    this.masterDefinition = { cells: {} };
    // Initializing Cell Data Map
    this.cellDataMap = this.masterDefinition.cells;
    this.accordionConfig = {};
    this.subscriptionLists = [];
  }


  /**
   * Initializes the component
   */
  public ngOnInit() {
    this.isLoading = true;
    this.initializeProperties();
    this.activatedRoute.params.subscribe(params => {
      this.isLoading = true;
      const docId = params['docId'];
      let retainState = null;
      if (this.activatedRoute.snapshot.queryParams && this.activatedRoute.snapshot.queryParams['retainState']) {
        retainState = this.activatedRoute.snapshot.queryParams['retainState'];
      }
      if (docId) { // ie, if navigated via route param
        this.dataService.setPrimarySelectedDocument(docId);
        this.navService.setPageTitle(this.entityConfig.getItemLabel() + ' # ' + docId);
      } else {
        // if there is no id provided, then go to the create flow
        this.dataService.displayMode = 'C';
        this.navService.setPageTitle(this.resourceService.get('lbl_create').concat(' ').concat(this.entityConfig.getItemLabel()));
      }

      if (retainState && retainState === '1') {
        this.retainState = true;
        this.dataService.displayMode = 'V';
      } else {
        this.retainState = false;
      }
      // Initialize the page once the metadata is available
      this.primarySearchService.fetchMetadata().subscribe(metadata => {
        this.initializePage();
      });
    });
    // Initialize label for the screen
    this.screenLabel = this.getItemLabel();
  }

  /**
   * Initializes the component properties
   */
  private initializeProperties() {
    this.dataService = <DetailedEntityDataService>this.serviceFactory.getDataService();
    this.entityConfig = this.dataService.getEntityConfiguration();
    this.buttonService = <DetailedEntityButtonService>this.serviceFactory.getButtonService();
    this.dataEditor = <DetailedEntityDataEditorService>this.serviceFactory.getDataEditor();
    this.primarySearchService = this.dataService.getPrimarySearchService();
    if (this.entityConfig.formPageConfig.keyEventHandler) {
      this.keyEventHandler = this.entityConfig.formPageConfig.keyEventHandler;
    } else {
      this.keyEventHandler = new DetailViewKeyEventHandlerService(this.dataEditor);
    }
  }

  // initializes the page based on the display mode.
  // C - create flow - This can only come from search page, so the metadata is already loaded
  // E - edit flow - This can only come from view page, so the metadata is already loaded
  // V - view flow - This can come from search page, recent items, direct url etc. Metadata may or may not be available
  protected initializePage() {
    /*[LFWM-2132] - to determine we are in which page when updating some values both in accordion and table
      or when performing any button actions */
    this.dataService.pageType = 'details';
    // create flow
    if (this.dataService.displayMode === 'C') {
      this.initializePageForCreate();
    } else {
      // this can be either a view or an edit flow
      const itemNumber: string = this.dataService.getPrimarySelectedDocument();
      /* [LFWM-2125] - dynamicButtonsExist - false and need to empty the dynamic buttons array
       before fetching dynamic buttons from backend when loading details page */
      this.buttonService.dynamicButtonsExist = false;
      this.buttonService.dynamicButtons = [];
      if (!itemNumber) {
        // if this is an invalid or a deleted item, show error
        this.alertService.error(this.resourceService.get('msg_no_itm'));
        this.isLoading = false;
        return;
      }

      // edit mode is supported only when retainState route param is true
      if (this.dataService.displayMode === 'E' && this.retainState) {
        // if (this.retainState) {
        //   this.refreshAccordion();
        // }
        this.initializePageForEdit(itemNumber);
      } else {
        this.dataService.displayMode = 'V';
        this.initializePageForView(itemNumber);
      }
    }
  }

  /**
   * this method is use to refresh the accoedion after save update or button action status changes happens
   */
  public refreshAccordion() {
    const primarySearchService = this.dataService.primarySearchService;
    primarySearchService
      .getItemDetailForId(primarySearchService.searchParams.idProperty, this.dataService.getPrimarySelectedDocument())
      .subscribe((response: any) => {
        // set cell data map
        if (!response || !response.cells) {
          this.alertService.error(this.resourceService.get('failed to reload data'));
          return;
        }
        response.cells.forEach((cell: any) => {
          cell.isEdited = false;
          this.dataService.primaryDataMap[cell.cName] = cell;
        });
        this.buttonService.initializeDetailPageButtons(this.dataService.primaryDataMap);
      });
  }



  // Initializes the page specific to the create flow
  protected initializePageForCreate() {
    if (this.primarySearchService.searchParams.autoGenerateIdProperty) {
      // If Auto Generat Detail Id, Item Number has to be generated
      const uniqueIdSubscription = this.primarySearchService.createUniqueId(this.entityConfig.getType())
        .subscribe((response: any) => {
          if (response && response.documentNumber) {
            const documentNumber = response.documentNumber;
            this.dataService.setPrimarySelectedDocument(documentNumber);
            this.screenLabel = this.getItemLabel();
            // create cell data map with default values
            this.cellDataMap = this.dataService.createNewRecord();
            this.initializePrimarySection(documentNumber);
            this.navService.preventNavigation = true;
          } else {
            this.alertService.error(this.entityConfig.getItemLabel() + ' '
              + this.resourceService.get('err_creation_failed'));
          }
        });

      this.subscriptionLists.push(uniqueIdSubscription);
    } else {
      // JIRA-824 - To clear the PrimarySelectedDocument Value
      this.dataService.setPrimarySelectedDocument('');
      // create cell data map with default values
      this.cellDataMap = this.dataService.createNewRecord();
      this.initializePrimarySection('');
      this.navService.preventNavigation = true;
    }

    // For screens with line items in details page
    if (this.dataService.entityConfig.getShowDetailLineItems()) {
      if (!this.retainState) {
        this.dataService.detailsSearchService = null;
      }
      this.initializeDetailTable(false);
    }
  }



  // Initializes the page specific to the edit flow. The data for the page is already loaded
  protected initializePageForEdit(selectedItem) {
    // if the page allows saved state, do not load the data from the server. Simply load the page
    this.cellDataMap = this.dataService.getPrimaryDataMap();
    this.initializePrimarySection(selectedItem);
    // For screens with line items in details page
    if (this.dataService.entityConfig.getShowDetailLineItems()) {
      this.initializeDetailTable(false);
    }
  }

  // Initializes the page specific to the view flow
  protected initializePageForView(selectedItem) {
    const cellDefinitionsSubscription = this.primarySearchService
      .getItemDetailForId(this.primarySearchService.searchParams.idProperty, selectedItem)
      .subscribe((response: any) => {
        // set cell data map
        if (!response || !response.cells) {
          this.alertService.error(this.resourceService.get('msg_invalid') + this.entityConfig.getItemLabel());
          this.navService.retainAlerts().navigateByMenuId(this.entityConfig.getType().toLowerCase());
          return;
        }
        response.cells.forEach((cell: any) => {
          cell.isEdited = false;
          this.cellDataMap[cell.cName] = cell;
        });
        this.initializePrimarySection(selectedItem);
        this.screenLabel = this.getItemLabel();
      });
    this.subscriptionLists.push(cellDefinitionsSubscription);

    // For screens with line items in details page
    if (this.dataService.entityConfig.getShowDetailLineItems()) {
      this.initializeDetailTable(true);
    }
  }

  // Method to define layout for each accordion. Categorized columnList is split into row definitions of 5 cells each.
  protected getAccordionLayout(propertyArray: Array<any>) {
    let accordionRow;
    const accordionDefinition = [];
    for (let x = 0; x < propertyArray.length; x++) {
      if (x % 5 === 0) {
        if (accordionRow && accordionRow.length === 5) {
          accordionDefinition.push(accordionRow);
        }
        accordionRow = [];
      }
      accordionRow.push(propertyArray[x]);
    }
    if (accordionRow && accordionRow.length !== 0) {
      for (let y = accordionRow.length; y < 5; y++) {
        accordionRow.push('dummy');
      }
      accordionDefinition.push(accordionRow);
    }
    return accordionDefinition;
  }

  // loads the primary section which includes all accordian groups
  protected initializePrimarySection(itemNumber) {
    this.isPrimaryDataLoaded = true;
    this.setUpAccordionConfig();
    this.dataService.setPrimaryDataMap(this.cellDataMap);
    this.buttonService.initializeButtonsForPage(this.cellDataMap, 'details');
    this.navService.addNonNavigableBreadCrumb(this.entityConfig.getBreadCrumbPrefix() + itemNumber);
  }

  private setUpAccordionConfig() {
    this.metadataMap = {};
    const columnsByGroup = {};
    const columnGroupList = [];
    this.accordionUserPreference = {};
    // Metadata for Primary & Secondary Accordions are readily available in Primary Search Service
    this.primarySearchService.getMetadata().columnList.map((column: any) => {
      /*If there is any hidden property for an accordion that should be eliminated*/
      if (this.entityConfig.formPageConfig.hiddenPropertiesForAccordion.length > 0 &&
        this.entityConfig.formPageConfig.hiddenPropertiesForAccordion.indexOf(column.propertyName) !== -1) {
        return;
      }
      // Metadata.columnList is iterated and categorized based on columnGroup
      if (!columnsByGroup[column.columnGroup]) {
        columnsByGroup[column.columnGroup] = [];
        if (column.columnGroup !== 'Primary' && column.columnGroup !== 'ACCORDION') {
          this.accordionUserPreference[column.columnGroup] = Object.keys(this.accordionUserPreference).length;
        }
        columnGroupList.push(column.columnGroup);
      }
      columnsByGroup[column.columnGroup].push(column.propertyName);
      /* commenting out the below code as we need to display the dropdown values in
      the order as we get in the metadata*/
      /* if (column.type === 'DROPDOWN') {
        column.values.sort((a: any, b: any) => {
          return (a.value > b.value) ? 1 : ((b.value > a.value) ? -1 : 0);
        });
      } */
      this.metadataMap[column.propertyName] = column;
    });

    if (this.cellDataMap.docType && this.cellDataMap.docType.cVal === 'A'
      && this.dataService.entityConfig.type === 'ASN') {
      /* If condition added for LFWM-1895.
      Adds a dummy columngroup for Dummy Accordion to be
       created in secondary accordion creaton logic under
        columnGroupList.forEach( below....
       show ASN Image only for ASN.i.e docType=A.
       (same component used in sku image)
       Hidden for all others xdock po etc..
       Dependent changes in accordion.component.html template
       <tr *ngIf="accordionConfig.columnGroup === 'Sku Image'"
       */
      columnGroupList.push('asn_image');
      columnsByGroup['asn_image'] = [];
    }

    this.secondaryAccordionList = [];
    this.tablePreference = this.primarySearchService.getTablePreference();
    if (Object.keys(this.tablePreference.columGroupConfig.configuration).length === 0) {
      this.tablePreference.columGroupConfig.configuration = this.accordionUserPreference;
    } else {
      this.accordionUserPreference = this.tablePreference.columGroupConfig.configuration;
    }
    columnGroupList.forEach((columnGroup: any) => {
      if (columnGroup === 'Primary') {
        this.primaryAccordion = this.createAccordion();
        this.primaryAccordion.propertyList = columnsByGroup[columnGroup];
        this.primaryAccordion.name = this.dataService.entityConfig.getItemLabel();
        this.primaryAccordion.isPrimaryAccordion = true;
      } else if (columnGroup !== 'ACCORDION') {
        const secondaryAccordion = this.createAccordion();
        secondaryAccordion.columnGroup = columnGroup;
        secondaryAccordion.propertyList = columnsByGroup[columnGroup];
        secondaryAccordion.name = this.resourceService.get(columnGroup);
        secondaryAccordion.activeIndex = this.accordionUserPreference[columnGroup];
        this.secondaryAccordionList.push(secondaryAccordion);
      }
    });
    if (this.secondaryAccordionList.length === 0) {
      this.dispSecAcc = true;
    }
    // this.customizeMetadataMap();
    this.dataService.primaryMetaDataMap = this.metadataMap;
    // the below code is added to override the metadata object
    this.dataService.overrideMetadataMap(this.dataService.displayMode);
    this.initializeTableColumnAttribute(this.primarySearchService);
    this.isLoading = false;
  }

  public getSecondaryAccordionList(): Array<any> {
    return this.secondaryAccordionList
      .sort((a: any, b: any) => {
        if (a.activeIndex < b.activeIndex) {
          return -1;
        } else if (a.activeIndex > b.activeIndex) {
          return 1;
        } else {
          return 0;
        }
      });
  }

  private createAccordion() {
    const accordionConfig = new AccordionConfig();
    accordionConfig.metaDataMap = this.metadataMap;
    accordionConfig.cellDataMap = this.cellDataMap;
    accordionConfig.isOpen = false;
    accordionConfig.dataService = this.dataService;
    accordionConfig.dataEditor = this.dataEditor;
    accordionConfig.keyEventHandler = this.keyEventHandler;
    return accordionConfig;
  }

  // Method to Customize Metadata for Display Only Changes
  private customizeMetadataMap() {
    // Overriding Metadata for facility to display it in ['Key - Value'] format
    // Below, Deep Clone has to be performed to avoid directly updating Metadata Object in Service
    if (!this.metadataMap['facility']) {
      return;
    }
    const facilityMetadata = JSON.parse(JSON.stringify(this.metadataMap['facility']));
    facilityMetadata.values.forEach((option) => {
      option.value = option.key + ' - ' + option.value;
    });
    this.metadataMap['facility'] = facilityMetadata;
  }

  public handleReorder($event: any) {
    let reorderTargetKey: string;
    let temp: any;
    if ($event.isDrop) {
      reorderTargetKey = $event.columnGroup;
      if (reorderTargetKey === this.reorderSourceKey) {
        return;
      }
      temp = this.tablePreference.columGroupConfig.configuration[this.reorderSourceKey];
      this.tablePreference.columGroupConfig.configuration[this.reorderSourceKey]
        = this.tablePreference.columGroupConfig.configuration[reorderTargetKey];
      this.tablePreference.columGroupConfig.configuration[reorderTargetKey] = temp;
      this.secondaryAccordionList.forEach((config: any) => {
        config.activeIndex = this.tablePreference.columGroupConfig.configuration[config.columnGroup];
      });
      this.accordionUserPreference = this.tablePreference.columGroupConfig.configuration;
      this.tablePreferences.saveTablePreferences(this.tablePreference.columGroupConfig)
        .subscribe((data: any) => {
          if (!this.tablePreference.columGroupConfig.preferenceId) {
            this.tablePreference.columGroupConfig.preferenceId = data.preferenceId;
          }
          this.alertService.clearAll().success(this.resourceService.get('msg_usr_pref_succ'));
        }, (error: any) => {
          this.alertService.clearAll().error(this.resourceService.get('msg_pref_sav_fail'));
        });
    } else {
      this.reorderSourceKey = $event.columnGroup;
      this.secondaryAccordionList.forEach((accordionConfig: any) => {
        accordionConfig.isOpen = false;
      });
    }
  }

  public getItemLabel() {
    // If the screen autogenerates ID in create page
    if (this.dataService.getPrimarySelectedDocument()) {
      return this.entityConfig.getBreadCrumbPrefix() + this.dataService.getPrimarySelectedDocument();
    } else {
      return this.entityConfig.getItemLabel(); // Screen name is set as label
    }
  }

  // initializes the line item detail table
  protected initializeDetailTable(loadDataFromServer) {
    // if the retainState flag is set, do not reinitialize the service. Use the service already loaded in the memory
    /* LFWM - 1387 : Removed the condition !this.retainState from if block
    to prevent re initialization of search context*/
    if (!this.dataService.getDetailsSearchService()) {
      this.dataService.initializeDetailsSearchContext();
    }

    this.detailsSearchService = this.dataService.getDetailsSearchService();
    // LFWM-3363 resetting the max line number for every new detail table
    this.dataService.maxIdValueForDetailTable = '';
    const metadataSubscription = this.detailsSearchService.fetchMetadata().subscribe((metadata) => {
      metadata.columnList.sort((a, b) => {
        return a.index - b.index;
      });
      if (loadDataFromServer) {
        this.loadDetailTable();
      }
      this.showDetailsAccordion = true;
      this.expandDetailsAccordion = true;
    });
    this.subscriptionLists.push(metadataSubscription);

    // Subscription for the Search Events from Details Search Service.
    const detailsSearchEventSubscription = this.detailsSearchService.receiveEvent()
      .subscribe((event: SearchEvent) => {
        this.buttonService.initializeButtonsForPage(this.cellDataMap, 'details');
        if (event.type === NEW_SEARCH_EVENT && this.detailsSearchService.getMetadata()) {
          // For NEW_SEARCH_EVENT, the Details Data Table will be displayed
          this.showDetailsAccordion = true;
          //        this.expandDetailsAccordion = true;
        } else if (event.type === NEW_ROW_EVENT) {
          // For NEW_ROW_EVENT
          this.dataService.handleAddNewLineItem(event);
        } else if (event.type === PAGE_OVERFLOW_EVENT) {
          // This event comes when a page overflow has occured. Save the corrent page and navigate to the next
          // page on success
          this.detailsSearchService.setNewPageEventInProgress(true);
          this.buttonService.performButtonActions('Save');
        }
      });
    this.subscriptionLists.push(detailsSearchEventSubscription);
    this.initializeTableColumnAttribute(this.detailsSearchService);
  }

  // Method to load Data Table in Details Accordion
  protected loadDetailTable() {
    // Setting Search Criteria in Details Search Service which is the input for Data Table in Details Accordion
    this.detailsSearchService.setSearchCriteria({
      conditions: [{
        mandatory: false,
        clauses: [{
          column: this.primarySearchService.searchParams.idProperty,
          operation: '=',
          value: this.dataService.getPrimarySelectedDocument(),
          logicalOperation: null
        }],
        logicalOperation: null
      }]
    });
    /* Invoking Search in DetailsSearchService. The above eventSubscription listens to
       the New Search Event fired as a result of the below method */
    this.detailsSearchService.performSearch();
  }

  // gets the edited cells among cell properties
  private getEditedCells(): Array<any> {
    const editedCells = [];
    Object.values(this.cellDataMap).forEach((cell: any) => {
      if (cell.isEdited) {
        editedCells.push(cell);
      }
    });
    return editedCells;
  }

  // method used to translate the labels
  public getLabel(key: any) {
    return this.resourceService.get(key);
  }

  // Method to return Cell Title corresponding to each property
  public getCellTitle(property: any): string {
    return this.resourceService.get(this.metadataMap[property].elementId);
  }

  // Method  to override default display format for each cell value (in VIEW Mode ONLY)
  public getCellDisplayValue(property: any): any {
    return ''; // this.dataService.getCellDisplayValue(property, this.isLoading);
  }

  public toggleSecondaryAccordion() {
    this.expandSecondaryAccordion = !this.expandSecondaryAccordion;
  }

  public toggleDetailsAccordion() {
    this.expandDetailsAccordion = !this.expandDetailsAccordion;
  }

  private openLookup(cells: any, property: any) {
    const row = { cells: this.cellDataMap };
    this.dataEditor.openLookup(row, property);
  }

  protected handleMainButtonPanelActions(action) {
    const isCreateFlow = (this.dataService.displayMode === 'C');
    if (isCreateFlow) {
      this.buttonService.handleButtonActionForCreate(action);
    } else {
      let haveUnsavedData;
      if (this.dataService.getDetailsSearchService()) {
        haveUnsavedData = (this.dataService.getDetailsSearchService().getEditedCellsPerRow().length > 0)
          || (this.getEditedCells().length > 0);
      } else {
        haveUnsavedData = this.getEditedCells().length > 0;
      }
      if (haveUnsavedData) {
        this.buttonService.handleButtonActionForEdit(action);
      } else {
        this.buttonService.performButtonActions(action);
      }
    }
  }

  // method used to fetch the column attribute from service
  public initializeTableColumnAttribute(searchService) {
    searchService.fetchTableColumnAttribute()
        .subscribe((tableColumnAttribute: any) => {
            searchService.setTableColumnAttribute(tableColumnAttribute);
        });
  }

  public ngOnDestroy() {
    if (this.subscriptionLists) {
      this.subscriptionLists.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
    this.dataService.handleDestroyDetails();
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/1_SCE_CI_CD_SIT/src/app/shared/components/searchable-entity/details-page/details-page.component.ts